import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FullScreenModal } from "../../components/FullScreenModal";
import { externalMapsUrl } from "../../lib/mapsLinks";

const TYPE_LABEL = { milestone: "Milestone", meeting: "Meeting", call: "Meeting", event: "Meeting", deadline: "Meeting" };

const BAR_COLORS = {
  milestone: "bg-[rgba(0,30,255,0.12)] text-[var(--t-text)]",
  call: "bg-[rgba(0,30,255,0.11)] text-[var(--t-text)]",
  event: "bg-[rgba(0,30,255,0.11)] text-[var(--t-text)]",
  deadline: "bg-[rgba(0,30,255,0.11)] text-[var(--t-text)]",
};

function hasClockTime(iso) {
  if (!iso) return false;
  const raw = String(iso);
  if (!raw.includes("T")) return false;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  return !(d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0);
}

function formatCalendarTime(iso) {
  if (!hasClockTime(iso)) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatCalendarDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function formatCalendarRange(startIso, endIso) {
  const start = formatCalendarTime(startIso);
  const end = formatCalendarTime(endIso);
  if (start && end && start !== end) return `${start} – ${end}`;
  return start || end || "—";
}

function startOfWeekMonday(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day + 6) % 7;
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function localMidnight(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysBetweenInclusive(a, b) {
  const ms = 86400000;
  const ua = localMidnight(a).getTime();
  const ub = localMidnight(b).getTime();
  return Math.round((ub - ua) / ms);
}

/** Week timeline segments for an event (date-only; optional `end` extends the bar). */
function segmentForWeek(event, weekStart) {
  const ws = localMidnight(weekStart);
  const we = addDays(ws, 6);
  const start = localMidnight(new Date(event.start));
  const rawEnd = event.end ? localMidnight(new Date(event.end)) : start;
  const end = rawEnd.getTime() < start.getTime() ? start : rawEnd;

  const clipStart = start > ws ? start : ws;
  const clipEnd = end < we ? end : we;
  if (clipStart > we || clipEnd < ws) return null;

  const colStart = Math.max(0, Math.min(6, daysBetweenInclusive(ws, clipStart)));
  const colEnd = Math.max(0, Math.min(6, daysBetweenInclusive(ws, clipEnd)));
  const startMs = new Date(event.start).getTime();
  return {
    colStart,
    colEnd: Math.max(colStart, colEnd),
    id: event.id,
    title: event.title,
    type: event.type || "event",
    startMs,
    assigned_to: event.assigned_to,
    event,
  };
}
function assignLanes(segments) {
  const sorted = [...segments].sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    if (a.colStart !== b.colStart) return a.colStart - b.colStart;
    if (a.colEnd !== b.colEnd) return a.colEnd - b.colEnd;
    return String(a.id).localeCompare(String(b.id));
  });
  const laneEnds = [];

  return sorted.map((seg) => {
    let lane = 0;
    while (lane < laneEnds.length) {
      if (seg.colStart > laneEnds[lane]) break;
      lane += 1;
    }
    if (lane === laneEnds.length) laneEnds.push(seg.colEnd);
    else laneEnds[lane] = seg.colEnd;
    return { ...seg, lane };
  });
}

export default function CalendarPage() {
  const { client, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const weekScrollRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [filter, setFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const params = isAdmin ? `?client_id=${client?.id}` : "";

  const load = useCallback(async () => {
    if (!client) return;
    try {
      if (isAdmin) {
        try {
          await api.post(`/admin/google-calendar/sync?client_id=${client.id}`);
        } catch {
          // Best-effort; calendar still loads from the last successful sync.
        }
      }
      const { data } = await api.get(`/portal/calendar${params}`);
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((a, b) => new Date(a.start) - new Date(b.start));
      setEvents(list);
    } catch {
      toast.error("Could not load calendar");
      setEvents([]);
    }
  }, [client, params, isAdmin]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 60000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const el = weekScrollRef.current;
    if (el) el.scrollLeft = 0;
  }, [weekStart]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(weekStart, i);
      return d;
    });
  }, [weekStart]);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "milestone") return events.filter((e) => e.type === "milestone");
    return events.filter((e) => e.type !== "milestone");
  }, [events, filter]);

  const monthHeading = useMemo(() => {
    const end = addDays(weekStart, 6);
    const m0 = weekStart.toLocaleDateString(undefined, { month: "long" });
    const y0 = weekStart.getFullYear();
    const m1 = end.toLocaleDateString(undefined, { month: "long" });
    const y1 = end.getFullYear();
    if (m0 === m1 && y0 === y1) return `${m0} ${y0}`;
    if (y0 === y1) return `${m0} – ${m1} ${y0}`;
    return `${m0} ${y0} – ${m1} ${y1}`;
  }, [weekStart]);

  const { segments, laneCount } = useMemo(() => {
    const raw = [];
    filtered.forEach((e) => {
      const s = segmentForWeek(e, weekStart);
      if (s) raw.push(s);
    });
    const placed = assignLanes(raw);
    const maxLane = placed.reduce((m, s) => Math.max(m, s.lane), -1);
    return { segments: placed, laneCount: Math.max(1, maxLane + 1) };
  }, [filtered, weekStart]);

  const rowHeight = 34;
  const headerH = 60;
  const pad = 8;
  const trackMinH = headerH + pad * 2 + laneCount * rowHeight;
  const isCurrentWeek =
    localMidnight(weekStart).getTime() === localMidnight(startOfWeekMonday(new Date())).getTime();

  const filterButtons = (
    <div className="flex flex-wrap justify-end gap-1 self-center md:self-auto" data-testid="event-filter">
      {["all", "milestone", "meeting"].map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => setFilter(f)}
          className={`px-3 py-2 text-xs uppercase tracking-widest ${filter === f ? "border-b-2 border-[var(--t-accent)]" : "text-[var(--t-meta)]"}`}
        >
          {f === "all" ? "All" : TYPE_LABEL[f] || f}
        </button>
      ))}
    </div>
  );

  const weekNav = (
    <div className="flex shrink-0 items-center justify-end gap-0.5 self-center sm:gap-1 md:self-auto">
      <button
        type="button"
        onClick={() => setWeekStart(addDays(weekStart, -7))}
        className="flex items-center justify-center px-3 py-2 text-xs uppercase tracking-widest text-[var(--t-meta)]"
        data-testid="prev-week"
        aria-label="Previous week"
      >
        <ChevronLeft size={16} strokeWidth={1.4} />
      </button>
      <button
        type="button"
        onClick={() => setWeekStart(startOfWeekMonday(new Date()))}
        className={`px-3 py-2 text-xs uppercase tracking-widest ${isCurrentWeek ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"}`}
        data-testid="today-btn"
      >
        Today
      </button>
      <button
        type="button"
        onClick={() => setWeekStart(addDays(weekStart, 7))}
        className="flex items-center justify-center px-3 py-2 text-xs uppercase tracking-widest text-[var(--t-meta)]"
        data-testid="next-week"
        aria-label="Next week"
      >
        <ChevronRight size={16} strokeWidth={1.4} />
      </button>
    </div>
  );

  if (!client) return <div className="p-12 t-meta">No client context.</div>;

  return (
    <div
      className="t-fade-up flex min-h-0 min-w-0 w-full max-w-full flex-col px-4 pb-4 pt-4 text-left sm:px-5 sm:pb-5 sm:pt-5 max-lg:h-[calc(100dvh-var(--portal-mobile-header-h,68px))] max-lg:max-h-[calc(100dvh-var(--portal-mobile-header-h,68px))] max-lg:flex-col max-lg:overflow-x-visible max-lg:overflow-y-hidden lg:overflow-x-hidden lg:px-7 lg:pb-0 lg:pt-7 lg:h-[calc(100dvh-var(--portal-sidebar-footer-h,0px))] lg:max-h-[calc(100dvh-var(--portal-sidebar-footer-h,0px))] lg:flex-col lg:overflow-hidden lg:overscroll-y-contain"
      data-testid="calendar-page"
    >
      {/* Mobile: full-bleed border; horizontal inset matches ProjectTracker (shell pad + px-1/sm:px-2). Vertical inset matches tracker-page pt (pt-3/sm:pt-6 below md). */}
      <div
        className="-mx-4 w-[calc(100%+2rem)] max-w-none shrink-0 overflow-x-auto border-b border-white/40 px-4 pb-px [-webkit-overflow-scrolling:touch] sm:-mx-5 sm:w-[calc(100%+2.5rem)] sm:px-5 md:hidden"
        data-testid="week-grid-header"
      >
        <div className="flex min-h-[44px] w-[max(100%,max-content)] flex-nowrap items-center gap-3 ps-1 pe-1 sm:gap-4 sm:ps-2 sm:pe-2">
          <div className="font-heading !font-light min-w-0 flex-1 basis-0 truncate text-lg sm:text-xl tracking-tight text-[var(--t-text)]">
            {monthHeading}
          </div>
          {weekNav}
        </div>
      </div>

      <div className="hidden md:block shrink-0" data-testid="week-grid-header-desktop">
        {/* Desktop: eyebrow row matches MessagesPage — direct child of page shell, not inside week scroll. */}
        <div className="mb-6 flex items-center justify-between">
          <div className="label-eyebrow" data-testid="upcoming-label-desktop">
            Upcoming
          </div>
          {filterButtons}
        </div>
        <div className="mb-3 flex items-center justify-between gap-4">
          <div className="font-heading !font-light min-w-0 text-lg sm:text-xl tracking-tight text-[var(--t-text)]">
            {monthHeading}
          </div>
          {weekNav}
        </div>
      </div>

      <div
        ref={weekScrollRef}
        className="max-w-full min-h-0 flex-1 overflow-y-hidden overflow-x-auto [-webkit-overflow-scrolling:touch] max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-1 max-md:flex-col md:flex md:h-full md:min-h-0 md:flex-col md:overflow-y-hidden lg:overflow-x-visible"
        data-testid="week-grid-scroll"
        role="region"
        aria-label="Week calendar, scroll horizontally on small screens to see all days"
      >
        <div
          className="w-full max-lg:w-[200%] max-lg:min-w-[200%] bg-transparent max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-col md:flex md:h-full md:min-h-0 md:flex-col lg:min-w-0"
          data-testid="week-grid"
        >
          <div className="grid shrink-0 grid-cols-7 border-t-0 md:border-t md:border-[var(--t-border)]">
            {days.map((d) => {
              const isToday = localMidnight(d).getTime() === localMidnight(new Date()).getTime();
              return (
                <div key={d.toISOString()} className="px-1 py-2 text-center border-l border-[var(--t-border)] first:border-l-0 sm:px-2 md:py-3">
                  <div className="label-eyebrow">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className={`font-heading !font-light text-xl sm:text-2xl tracking-tight mt-1 ${isToday ? "text-[var(--t-accent)]" : ""}`}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          <div
            className="relative w-full max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-1 max-md:flex-col max-md:overflow-hidden md:flex md:h-full md:min-h-0 md:flex-1 md:flex-col md:overflow-hidden"
            data-testid="week-track-scroll"
          >
            <div
              className="relative flex h-full max-h-full min-h-0 w-full flex-col overflow-hidden max-md:flex-1 md:flex-1"
              style={{ minHeight: trackMinH }}
            >
              <div className="absolute inset-0 grid grid-cols-7 pointer-events-none" aria-hidden>
                {days.map((d) => (
                  <div key={d.toISOString()} className="border-l border-[var(--t-border)] first:border-l-0 bg-transparent" />
                ))}
              </div>

              <div
                className="relative z-[1] min-h-0 flex-1 px-1 pb-0 pt-1.5 sm:px-2"
                style={{ minHeight: Math.max(trackMinH - headerH + 8, 0) }}
              >
              {segments.map((seg) => {
                const span = seg.colEnd - seg.colStart + 1;
                const leftPct = (seg.colStart / 7) * 100;
                const widthPct = (span / 7) * 100;
                const top = pad + seg.lane * rowHeight;
                const barClass = BAR_COLORS[seg.type] || BAR_COLORS.event;
                const timeLabel = formatCalendarTime(seg.event?.start);
                return (
                  <div
                    key={seg.id}
                    className="absolute flex h-[28px] min-h-[28px] max-h-[28px] items-center gap-1.5"
                    style={{
                      left: `calc(${leftPct}% + 4px)`,
                      width: `calc(${widthPct}% - 8px)`,
                      top,
                      maxWidth: "calc(100% - 8px)",
                    }}
                    data-testid={`event-${seg.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(seg.event)}
                      className={`flex h-full min-w-0 flex-1 items-center overflow-hidden neo-panel px-2 shadow-none backdrop-blur-md [-webkit-backdrop-filter:blur(12px)] sm:px-2.5 py-0 text-left ${barClass}`}
                      aria-label={`${seg.title}${timeLabel ? `, ${timeLabel}` : ""}`}
                    >
                      <div className="min-w-0 flex-1 font-light text-[11px] sm:text-xs tracking-tight leading-tight truncate">
                        <span className="truncate">{seg.title}</span>
                      </div>
                    </button>
                    {timeLabel ? (
                      <span className="shrink-0 text-[10px] sm:text-[11px] font-light tabular-nums text-[var(--t-meta)]">
                        {timeLabel}
                      </span>
                    ) : null}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedEvent ? (
        <FullScreenModal
          onClose={() => setSelectedEvent(null)}
          ariaLabel={selectedEvent.title || "Calendar event"}
          contentClassName="p-4 sm:p-8 max-h-[90vh] overflow-y-auto"
          testid="calendar-event-modal"
        >
          <div className="label-eyebrow mb-3">
            {selectedEvent.type === "milestone"
              ? TYPE_LABEL.milestone
              : (TYPE_LABEL[selectedEvent.type] || TYPE_LABEL.meeting)}
          </div>
          <h2 className="font-heading text-xl sm:text-2xl mb-6 tracking-tight">{selectedEvent.title}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="label-eyebrow mb-1">Date</dt>
              <dd className="text-sm font-light text-[var(--t-text)]">{formatCalendarDate(selectedEvent.start)}</dd>
            </div>
            <div>
              <dt className="label-eyebrow mb-1">Time</dt>
              <dd className="text-sm font-light text-[var(--t-text)]">{formatCalendarRange(selectedEvent.start, selectedEvent.end)}</dd>
            </div>
            {selectedEvent.type !== "milestone" && selectedEvent.location ? (
              <div>
                <dt className="label-eyebrow mb-1">Location</dt>
                <dd className="text-sm font-light text-[var(--t-text)] whitespace-pre-wrap">
                  <a
                    href={externalMapsUrl(selectedEvent.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--t-accent)] hover:underline"
                  >
                    {selectedEvent.location}
                  </a>
                </dd>
              </div>
            ) : null}
            {selectedEvent.notes ? (
              <div>
                <dt className="label-eyebrow mb-1">Notes</dt>
                <dd className="text-sm font-light text-[var(--t-text)] whitespace-pre-wrap">{selectedEvent.notes}</dd>
              </div>
            ) : null}
          </dl>
          <div className="mt-8">
            <button
              type="button"
              onClick={() => setSelectedEvent(null)}
              className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] min-h-[44px] px-4"
            >
              Close
            </button>
          </div>
        </FullScreenModal>
      ) : null}
    </div>
  );
}
